"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.aggregateRoomGroups = void 0;
const currencyFormatter_1 = require("../currencyFormatter");
const aggregateRoomGroups = (roomSummaries) => {
    const parseCurrency = (value) => typeof value === "string"
        ? Number(value.replace(/[^\d.-]/g, "")) || 0
        : Number(value) || 0;
    // Group by rate_id + bed_configuration
    const grouped = roomSummaries.reduce((acc, room) => {
        const key = `${room.rate_id}__${room.bed_configuration?.trim() || "unknown"}`;
        if (!acc[key])
            acc[key] = { ...room, items: [] };
        acc[key].items.push(room);
        return acc;
    }, {});
    const aggregated = Object.values(grouped).map((group, index) => {
        const { currency, nights } = group;
        const rooms = group.items;
        const totalRooms = rooms.length;
        const totalGuests = rooms.reduce((sum, r) => sum + (r.occupancy || 0), 0);
        // Sell prices
        const totalSellOriginal = rooms.reduce((sum, r) => sum + parseCurrency(r.total_price), 0);
        const totalSellDiscounted = rooms.reduce((sum, r) => sum + parseCurrency(r.total_price_discounted), 0);
        // Cost prices
        const totalCostOriginal = rooms.reduce((sum, r) => sum + parseCurrency(r.total_cost_price), 0);
        const totalCostDiscounted = rooms.reduce((sum, r) => sum + parseCurrency(r.total_cost_price_discounted), 0);
        // Margins
        const totalMargin = rooms.reduce((sum, r) => sum + parseCurrency(r.total_margin), 0);
        // Base room values
        const first = rooms[0];
        const originalPerRoom = parseCurrency(first.total_price);
        const discountedPerRoom = parseCurrency(first.total_price_discounted);
        const effectivePerRoom = Math.min(originalPerRoom, discountedPerRoom);
        const isDiscounted = discountedPerRoom < originalPerRoom;
        // Effective totals
        const effectiveGroupSellPrice = Math.min(totalSellOriginal, totalSellDiscounted);
        const effectiveGroupCostPrice = Math.min(totalCostOriginal, totalCostDiscounted);
        // Nightly rates
        const ratePerNightOriginal = originalPerRoom / nights;
        const ratePerNightDiscounted = discountedPerRoom / nights;
        const ratePerNight = Math.min(ratePerNightOriginal, ratePerNightDiscounted);
        // Unique rate codes
        const uniqueRateCodes = [
            ...new Set(rooms.map((r) => r.rate_code).filter(Boolean)),
        ].join(", ");
        return {
            index: index + 1,
            rate_id: group.rate_id,
            bed_configuration: group.bed_configuration?.trim() || "Unknown",
            room_type: group.room_type,
            board: group.board,
            view: group.view,
            rate_code: uniqueRateCodes,
            nights,
            total_rooms: totalRooms,
            total_guests: totalGuests,
            currency,
            is_discounted: isDiscounted,
            summary: `${totalRooms} x ${group.summary}`,
            // Sell rate (customer-facing)
            room_rate_per_night: (0, currencyFormatter_1.currencyFormatter)(currency, ratePerNight, false) || "",
            room_rate_per_night_original: (0, currencyFormatter_1.currencyFormatter)(currency, ratePerNightOriginal, false) || "",
            room_rate_per_night_discounted: (0, currencyFormatter_1.currencyFormatter)(currency, ratePerNightDiscounted, false) || "",
            total_price_per_room: (0, currencyFormatter_1.currencyFormatter)(currency, effectivePerRoom, false) || "",
            total_price_per_room_original: (0, currencyFormatter_1.currencyFormatter)(currency, originalPerRoom, false) || "",
            total_price_per_room_discounted: (0, currencyFormatter_1.currencyFormatter)(currency, discountedPerRoom, false) || "",
            total_price_label: `Total price/room (${nights} nights)`,
            // Sell group totals
            total_group_price: (0, currencyFormatter_1.currencyFormatter)(currency, effectiveGroupSellPrice, false) || "",
            total_group_price_original: (0, currencyFormatter_1.currencyFormatter)(currency, totalSellOriginal, false) || "",
            total_group_price_discounted: (0, currencyFormatter_1.currencyFormatter)(currency, totalSellDiscounted, false) || "",
            // Cost group totals
            total_group_cost_price: (0, currencyFormatter_1.currencyFormatter)(currency, effectiveGroupCostPrice, false) || "",
            total_group_cost_price_original: (0, currencyFormatter_1.currencyFormatter)(currency, totalCostOriginal, false) || "",
            total_group_cost_price_discounted: (0, currencyFormatter_1.currencyFormatter)(currency, totalCostDiscounted, false) || "",
            // Margins
            total_group_margin: (0, currencyFormatter_1.currencyFormatter)(currency, totalMargin, false) || "",
            // Display helper
            summary_label: `${totalRooms} room${totalRooms > 1 ? "s" : ""} (${totalGuests} guest${totalGuests > 1 ? "s" : ""})`,
            // Raw grouped rooms
            rooms: rooms,
        };
    });
    return aggregated;
};
exports.aggregateRoomGroups = aggregateRoomGroups;
